import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import VendorsService from '../../../../../services/data/vendors';

// props
import { IVendorResourceShortProps } from '../../../../../props/data/vendors';

// components
import { ActionButton, CommandBar, DefaultButton, ICommandBarItemProps, PrimaryButton, Stack } from '@fluentui/react';
import moment from 'moment';
import Tag from '../../../../uiframeworks/tag';
import VerifyVendorForm from '../form/verify';
import RejectVendorForm from '../form/reject';
import ResetVendorverificationStatusForm from '../form/resetVerification';
import Text from '../../../../typography/text';
import LoadingComponent from '../../../../feedbacks/loading';
import Label from '../../../../typography/label';
import DetailsStack, { ISectionTabProps, IStackSectionProps } from '../../../../uiframeworks/detailsStack';
import VendorForm from './../form';
import VendorShipsList from '../../ships/list';
import VendorTrucksList from '../../trucks/list';
import VendorTrainsList from '../../trains/list';
import ComingSoon from '../../../../uiframeworks/comingSoon';
import VendorDocumentListComponent from '../../documents/list';
import PermissionsService from '../../../../../services/permissions';
import NoAccess from '../../../../uiframeworks/noAccess';
import ExternalBankAccountsList from '../../../../data/bankaccounts/external/list';
import VendorPICsList from './pic';
import BankAccountsList from '../../../../bankaccounts/general/list';
import InactivateVendorForm from '../form/inactivate';
import ActivateVendorForm from '../form/activate';
import VendorServiceForm from '../form/services/index';
import VendorDetailsServices from './services';
import VendorDetailsApprovals from './approvals';
import VendorDepositDetails from './deposits';
import VendorOrdersList from '../../orders/list';
import VendorSummaryDashboard from './summaryCards';
import VendorOutcomeInvoicesList from '../../invoices/list';

interface IVendorDetailsProps {
    vendorId: string;
}

const VendorDetails: React.FC<IVendorDetailsProps> = (props: IVendorDetailsProps) => {
    const { banner, topbar, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [vendor, setVendor] = React.useState<IVendorResourceShortProps>();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [activeTabKey, setActiveTabKey] = React.useState<string>('related_orders');
    const [invoiceStatusFilter, setInvoiceStatusFilter] = React.useState<'all' | 'paid' | 'unpaid'>('all');

    const hasPermission = PermissionsService.hasPermission(['vendors.read.all'], user.permissions)

    useEffect(() => {
        _onGetVendor();
    }, []);

    const _onGetVendor = async () => {
        try {
            setLoaded(false);

            const _vendor = await VendorsService.get(props.vendorId);
            setVendor(_vendor);

            topbar.show(_vendor.name, [
                { key: "home", icon: faHome, href: "/" },
                { key: "operationals", text: 'Operationals' },
                { key: "vendors", text: 'Vendors', href: "/operationals/vendors" },
                { key: props.vendorId, text: _vendor.name }
            ]);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vendor_error',
                text: 'Failed to get vendor details. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        const hasVerifyPermission = PermissionsService.hasPermission(['vendors.verification.verify'], user.permissions);
        const hasVerifyWithRiskPermission = PermissionsService.hasPermission(['vendors.verification.verify_with_risk'], user.permissions);
        const hasRejectPermission = PermissionsService.hasPermission(['vendors.verification.reject'], user.permissions);
        const hasResetPermission = PermissionsService.hasPermission(['vendors.verification.reset'], user.permissions);

        if (PermissionsService.hasPermission(['vendors.update.all'], user.permissions) && vendor?.active) {
            items.push({
                key: 'update',
                text: 'Update details',
                onRender: () => <PrimaryButton text={'Update Details'} onClick={() => setActiveSurface('update')} styles={{ root: { marginRight: 8 } }} />
            });
        }

        // Approval buttons when pending: 2 buttons (Verified & Not Verified)
        if (vendor?.active && vendor?.verificationStatus === 'pending_verification') {
            if (hasVerifyPermission) {
                items.push({
                    key: 'verify',
                    text: 'Set as Verified',
                    onRender: () => (
                        <PrimaryButton
                            text={'Set as Verified'}
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={() => setActiveSurface('verification.verify')}
                            styles={{ root: { marginRight: 8 } }}
                        />
                    )
                });
            }
            if (hasRejectPermission) {
                items.push({
                    key: 'reject',
                    text: 'Set as Not Verified',
                    onRender: () => (
                        <DefaultButton
                            text={'Set as Not Verified'}
                            iconProps={{ iconName: 'Cancel' }}
                            onClick={() => setActiveSurface('verification.reject')}
                            styles={{
                                root: { color: '#d13438', borderColor: '#d13438', marginRight: 8 },
                                rootHovered: { color: '#a80000', borderColor: '#a80000', backgroundColor: '#fde7e9' }
                            }}
                        />
                    )
                });
            }
        }

        // Renewal button when expired
        if (vendor?.active && vendor?.verificationStatus === 'expired') {
            if (hasVerifyPermission || hasVerifyWithRiskPermission) {
                items.push({
                    key: 'renewal_contract',
                    text: 'Renewal Contract',
                    onRender: () => (
                        <PrimaryButton
                            text={'Renewal Contract'}
                            iconProps={{ iconName: 'Refresh' }}
                            onClick={() => setActiveSurface('verification.renewal')}
                            styles={{
                                root: { backgroundColor: '#d83b01', borderColor: '#d83b01', marginRight: 8 },
                                rootHovered: { backgroundColor: '#b83201', borderColor: '#b83201' },
                                rootPressed: { backgroundColor: '#a32c01', borderColor: '#a32c01' }
                            }}
                        />
                    )
                });
            }
        }

        // Extend approval button when verified
        if ((hasVerifyPermission || hasVerifyWithRiskPermission) && vendor?.active && (vendor?.verificationStatus === 'verified' || vendor?.verificationStatus === 'verified_with_risk')) {
            items.push({
                key: 'extend_approval',
                text: 'Extend Approval',
                onRender: () => (
                    <DefaultButton
                        text={'Extend Approval'}
                        iconProps={{ iconName: 'Clock' }}
                        onClick={() => setActiveSurface('extend_approval')}
                        styles={{
                            root: {
                                marginRight: 8,
                                ...(vendor?.isApprovalExpired ? { borderColor: '#d83b01', color: '#d83b01' } : {})
                            },
                            rootHovered: vendor?.isApprovalExpired ? { borderColor: '#a32c01', color: '#a32c01', backgroundColor: '#fde7e9' } : undefined
                        }}
                    />
                )
            });

            if (hasResetPermission) {
                farItems.push({
                    key: 'reset_verification',
                    text: 'Reset Verification',
                    onRender: () => <ActionButton text={'Reset Verification'} iconProps={{ iconName: 'Reset' }} onClick={() => setActiveSurface('verification.reset')} />
                });
            }
        }

        if (PermissionsService.hasPermission(['vendors.delete.all'], user.permissions) && vendor?.active) {
            farItems.push({
                key: 'inactivate',
                text: 'Set as inactive',
                onRender: () => <ActionButton text={'Set as inactive'} onClick={() => setActiveSurface('inactivate')} />
            });
        }

        if (PermissionsService.hasPermission(['vendors.delete.all'], user.permissions) && !vendor?.active) {
            items.push({
                key: 'activate',
                text: 'Re-activate',
                onRender: () => <PrimaryButton text={'Re-activate'} onClick={() => setActiveSurface('activate')} />
            });
        }

        return { items, farItems };
    }

    const getVendorPropertiesSection = (_vendor: IVendorResourceShortProps): IStackSectionProps => {
        return {
            header: {
                title: "Details"
            },
            key: 'vendor_details',
            isCard: true,
            content: <Stack tokens={{ childrenGap: 20 }}>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Unique Id</Label>
                        <Text>{_vendor.id.toUpperCase()}</Text>
                    </Stack>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Name</Label>
                        <Text>{_vendor.name}</Text>
                    </Stack>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Phone Number</Label>
                        <Text>{_vendor.phoneNumber || "-"}</Text>
                    </Stack>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Address</Label>
                        <Text>{_vendor.address || "-"}</Text>
                    </Stack>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Approval Status</Label>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign={'center'}>
                            {_vendor.verificationStatus === 'pending_verification' && <Tag size={'small'} variant={'warning'} text={'Pending'} />}
                            {_vendor.verificationStatus === 'verified' && (
                                _vendor.isApprovalExpired ? (
                                    <Tag size={'small'} variant={'error'} text={'Approval Expired'} />
                                ) : (
                                    <Tag size={'small'} variant={'success'} text={'Verified'} />
                                )
                            )}
                            {_vendor.verificationStatus === 'verified_with_risk' && (
                                _vendor.isApprovalExpired ? (
                                    <Tag size={'small'} variant={'error'} text={'Approval Expired'} />
                                ) : (
                                    <Tag size={'small'} variant={'warning'} text={'Verified with Risk'} />
                                )
                            )}
                            {_vendor.verificationStatus === 'expired' && <Tag size={'small'} variant={'error'} text={'Expired (Butuh Renewal)'} />}
                            {_vendor.verificationStatus === 'rejected' && <Tag size={'small'} variant={'error'} text={'Rejected'} />}
                        </Stack>
                    </Stack>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Label size={'small'}>Periode Approval / Kontrak</Label>
                        <Text className={_vendor.isApprovalExpired || _vendor.verificationStatus === 'expired' ? 'color-red' : undefined}>
                            {_vendor.approvedFrom && _vendor.approvedUntil
                                ? `${moment(_vendor.approvedFrom).format('DD/MM/YYYY')} s/d ${moment(_vendor.approvedUntil).format('DD/MM/YYYY')}`
                                : (_vendor.approvedUntil
                                    ? `s/d ${moment(_vendor.approvedUntil).format('DD/MM/YYYY')}`
                                    : "-")}
                            {_vendor.isApprovalNotStarted ? ' (Belum Aktif)' : ''}
                            {(_vendor.isApprovalExpired || _vendor.verificationStatus === 'expired') ? ' (Expired)' : ''}
                        </Text>
                    </Stack>
                </Stack>
                {_vendor.verifiedAt && (
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack styles={{ root: { width: '50%' } }}>
                            <Label size={'small'}>Verified At</Label>
                            <Text>{moment(_vendor.verifiedAt).format('DD MMMM YYYY, HH:mm')}</Text>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        }
    }

    const _onSummaryCardClick = (target: 'orders' | 'expenses_all' | 'expenses_paid' | 'expenses_unpaid') => {
        if (target === 'orders') {
            setActiveTabKey('related_orders');
        } else if (target === 'expenses_all') {
            setActiveTabKey('vendor_invoices');
            setInvoiceStatusFilter('all');
        } else if (target === 'expenses_paid') {
            setActiveTabKey('vendor_invoices');
            setInvoiceStatusFilter('paid');
        } else if (target === 'expenses_unpaid') {
            setActiveTabKey('vendor_invoices');
            setInvoiceStatusFilter('unpaid');
        }

        const tabsElement = document.getElementById('vendor-details-tabs-section');
        if (tabsElement) {
            tabsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getVendorSummarySection = (_vendor: IVendorResourceShortProps): IStackSectionProps => {
        return {
            key: 'vendor_summary_dashboard',
            isCard: false,
            content: <VendorSummaryDashboard vendorId={_vendor.id} onCardClick={_onSummaryCardClick} />
        };
    };

    const getAssetsRecapSection = (_vendor: IVendorResourceShortProps): IStackSectionProps[] => {
        return [
            {
                header: {
                    title: "Approval Histories"
                },
                key: 'approval_histories',
                isCard: true,
                content: <VendorDetailsApprovals vendor={_vendor} />
            },
            {
                header: {
                    title: "Services",
                    buttons: [{ text: 'Add service', iconProps: { iconName: 'Add' }, onClick: () => setActiveSurface('services.add') }]
                },
                key: 'assets_recap',
                isCard: true,
                content: <VendorDetailsServices vendorId={_vendor.id} />
            }
        ]
    }

    const getRelatedItemsSection = (_vendor: IVendorResourceShortProps): IStackSectionProps => {
        const tabs: ISectionTabProps[] = [];

        tabs.push({
            key: 'related_orders',
            title: 'Related Order(s)',
            content: <Stack>
                <VendorOrdersList vendorId={_vendor.id} />
            </Stack>
        });

        if (PermissionsService.hasPermission(['outcomes.invoices.read.all'], user.permissions) ||
            PermissionsService.hasPermission(['superuser', 'superadmin'], user.roles)) {
            tabs.push({
                key: 'vendor_invoices',
                title: 'Invoice',
                content: <Stack>
                    <VendorOutcomeInvoicesList
                        vendorId={_vendor.id}
                        statusFilter={invoiceStatusFilter}
                        onStatusFilterChange={setInvoiceStatusFilter}
                    />
                </Stack>
            });
        }

        if (PermissionsService.hasPermission(['ships.read.all'], user.permissions)) {
            tabs.push({
                key: 'related_ships',
                title: 'Related Ship(s)',
                content: <Stack>
                    <VendorShipsList vendorId={_vendor.id} />
                </Stack>
            });
        }

        if (PermissionsService.hasPermission(['trucks.read.all'], user.permissions)) {
            tabs.push({
                key: 'related_trucks',
                title: 'Related Truck(s)',
                content: <Stack>
                    <VendorTrucksList vendorId={_vendor.id} />
                </Stack>
            });
        }

        if (PermissionsService.hasPermission(['ships.read.all'], user.permissions)) {
            tabs.push({
                key: 'related_trains',
                title: 'Related Train(s)',
                content: <Stack>
                    <VendorTrainsList vendorId={_vendor.id} />
                </Stack>
            });
        }

        tabs.push({
            key: 'pics',
            title: 'PICs',
            content: <Stack>
                <VendorPICsList vendorId={_vendor.id} />
            </Stack>
        });

        tabs.push({
            key: 'bank_accounts',
            title: 'Bank Account(s)',
            content: <Stack>
                <BankAccountsList vendorId={_vendor.id} />
            </Stack>
        });

        tabs.push({
            key: 'vendor_documents',
            title: 'Document(s)',
            content: <Stack>
                <VendorDocumentListComponent vendorId={_vendor.id} />
            </Stack>
        });

        if (PermissionsService.hasPermission(['deposits.read.all'], user.permissions)) {
            tabs.push({
                key: 'vendor_deposits',
                title: 'Deposit',
                content: <Stack>
                    <VendorDepositDetails vendorId={_vendor.id} />
                </Stack>
            });
        }

        return {
            key: 'vendor_tabs',
            isCard: true,
            selectedTabKey: activeTabKey,
            onTabClick: (key) => setActiveTabKey(key),
            tabs
        }
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onGetVendor() }
    }

    const canViewAnyStats = (
        PermissionsService.hasPermission(['orders.read.all', 'orders.expenses.read.all', 'outcomes.invoices.read.all'], user.permissions) ||
        PermissionsService.hasPermission(['superuser', 'superadmin'], user.roles)
    );

    return <Stack tokens={{ childrenGap: 20 }}>
        {!loaded ? <LoadingComponent label='Retrieving vendor details ...' labelPosition='right' spinnerPosition='baseline' /> : null}
        {loaded && vendor && hasPermission ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack tokens={{ childrenGap: 20 }}>
                <DetailsStack left={canViewAnyStats ? [getVendorPropertiesSection(vendor), getVendorSummarySection(vendor)] : [getVendorPropertiesSection(vendor)]} right={getAssetsRecapSection(vendor)} />
                <div id="vendor-details-tabs-section">
                    <DetailsStack left={[getRelatedItemsSection(vendor)]} />
                </div>
            </Stack>
            {activeSurface === 'update' ? <VendorForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'inactivate' ? <InactivateVendorForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'activate' ? <ActivateVendorForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'services.add' && vendor ? <VendorServiceForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
            {(activeSurface === 'verification.verify' || activeSurface === 'verification.verify_with_risk') && vendor ? (
                <VerifyVendorForm
                    vendorId={vendor.id}
                    defaultStatus={activeSurface === 'verification.verify_with_risk' ? 'verified_with_risk' : 'verified'}
                    onDismissed={_onSurfaceDismissed}
                />
            ) : null}
            {activeSurface === 'verification.renewal' && vendor ? (
                <VerifyVendorForm
                    vendorId={vendor.id}
                    defaultStatus={vendor.verificationStatus === 'verified_with_risk' ? 'verified_with_risk' : 'verified'}
                    isExtension={true}
                    onDismissed={_onSurfaceDismissed}
                />
            ) : null}
            {activeSurface === 'extend_approval' && vendor ? (
                <VerifyVendorForm
                    vendorId={vendor.id}
                    defaultStatus={vendor.verificationStatus === 'verified_with_risk' ? 'verified_with_risk' : 'verified'}
                    isExtension={true}
                    onDismissed={_onSurfaceDismissed}
                />
            ) : null}
            {activeSurface === 'verification.reject' && vendor ? <RejectVendorForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'verification.reset' && vendor ? <ResetVendorverificationStatusForm vendorId={vendor.id} onDismissed={_onSurfaceDismissed} /> : null}
        </> : null}
        {!hasPermission ? <NoAccess /> : null}
    </Stack>;
};

export default VendorDetails;
